/* eslint-disable no-console */
import _ from 'lodash'
import sanitizeHtml from 'sanitize-html'

export { arrayToMap, arrayToDirectMap, wait } from './helpers.no-deps'

export function cloneObject<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj))
}

export function promisify<T>(context: any, method: (...args: any[]) => void, ...args: any[]): Promise<T> {
    return new Promise((resolve, reject) => {
        const callback = (error: Error, result: T) => {
            if (error) reject(error)
            resolve(result)
        }

        args.push(callback)
        method.apply(context, args)
    })
}

/*
 * This function will execute all the promises in the given promise array, 2 at a time
 */
export function promiseAll(promises: Array<() => Promise<void>>, count: number) {
    return new Promise((resolve, reject) => {
        function next(): Promise<void> {
            if (consume.length) {
                return consume.pop()().then(next, next)
            }
        }

        const start = _.take(promises, count)
        const consume = _.drop(promises, count)

        Promise.all(start.map((promise) => promise().then(next, next))).then(resolve, reject)
    })
}

/*
 * Deletes all nulls from an object without side effects
 */
export function removeNulls<T>(object: T): T {
    const subObject = _.cloneDeep(object)
    ;(function recurse(o: any) {
        Object.getOwnPropertyNames(o).forEach((item) => {
            if (o[item] === null) delete o[item]
            else if (typeof o[item] === 'object') recurse(o[item])
        })
    })(subObject)

    return subObject
}

/*
 * Checks that the given time range includes now
 */
export function checkTimeRange<T extends TDateRange>(range: T) {
    const now = new Date()
    return (
        (typeof range.startDate === 'undefined' || range.startDate < now) &&
        (typeof range.endDate === 'undefined' || range.endDate > now)
    )
}

/*
 *  Validates if a given string is a valid ISO 8601 date-time string
 */
export function validateIso8601String(value: string) {
    // Convert to date object and back, then check against original value
    const parsed = new Date(Date.parse(value))
    try {
        if (parsed.toISOString() !== value) {
            throw new Error()
        }
    } catch {
        console.error(`Invalid ISO 8601 string '${value}'`)
        return false
    }
    return true
}

type TStringRange = { startDate?: string; endDate?: string }
type TDateRange = { startDate?: Date; endDate?: Date }

/*
 * Converts a string range to a date range
 * {startDate: string, endDate: string} => {startDate: Date, endDate: Date}
 */
export function stringRangeToDateRange<T extends TStringRange>(range: T): T & TDateRange {
    const newRange = {
        startDate: new Date(range.startDate),
        endDate: new Date(range.endDate),
    }
    // Generics and spreads don't work well together
    // https://github.com/Microsoft/TypeScript/issues/10727
    const result = Object.assign({}, range, newRange) // eslint-disable-line prefer-object-spread
    if (typeof range.startDate === 'undefined') delete result.startDate
    if (typeof range.endDate === 'undefined') delete result.endDate
    return result
}

/** Typesafe pick and omit */
export function pick<T extends object, Keys extends keyof T>(object: T, ...keys: Keys[]): Pick<T, Keys> {
    return _.pick(object, keys)
}

export function omit<T extends object, Keys extends keyof T>(object: T, ...keys: Keys[]): Omit<T, Keys> {
    return _.omit(object, keys)
}

export function omitArray<T extends object, Keys extends keyof T>(object: T[], ...keys: Keys[]): Array<Omit<T, Keys>> {
    return object.map((properties) => _.omit(properties, keys))
}

export function pickArray<T extends object, Keys extends keyof T>(object: T[], ...keys: Keys[]): Array<Pick<T, Keys>> {
    return object.map((properties) => _.pick(properties, keys))
}

/** Curried/higher order functions */
export function select<T extends object, Keys extends keyof T>(...keys: Keys[]): (object: T) => Pick<T, Keys> {
    return (object) => _.pick(object, keys)
}

export function selectArray<T extends object, Keys extends keyof T>(
    ...keys: Keys[]
): (objects: T[]) => Array<Pick<T, Keys>> {
    return (objects) => pickArray(objects, ...keys)
}

export function exclude<T extends object, Keys extends keyof T>(...keys: Keys[]): (object: T) => Omit<T, Keys> {
    return (object) => _.omit(object, keys)
}

export function excludeArray<T extends object, Keys extends keyof T>(
    ...keys: Keys[]
): (objects: T[]) => Array<Omit<T, Keys>> {
    return (objects) => omitArray(objects, ...keys)
}

export function get<T, Key extends keyof T>(key: Key): (object: T) => T[Key] {
    return (object) => object[key]
}

/* Typesafe order by function */
export function orderBy<T, Keys extends keyof T>(array: T[], ...keys: Keys[]): T[] {
    return _.orderBy(array, keys)
}

export function hashStringToInt(str: string) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = (hash << 5) - hash + char // eslint-disable-line no-bitwise
        // Convert to 32bit integer
        hash = hash & hash // eslint-disable-line no-bitwise
    }
    return hash
}

export function compareNumsAsc(a: number, b: number) {
    return a - b
}

export const isHtmlSafe = (text: string) =>
    sanitizeHtml(text, {
        allowedTags: [],
        parser: {
            decodeEntities: false,
        },
    }) === text

export function generateRandomHexColor() {
    return '#' + Math.floor(Math.random() * 16777215).toString(16)
}

export function symmetricDifference<T>(left: T[], right: T[]): T[] {
    const union = _.union(left, right)
    const intersection = _.intersection(left, right)
    return _.difference(union, intersection)
}

export function waitForDomElem(
    selector: any,
    options: MutationObserverInit = {
        childList: true,
        subtree: true,
    }
) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) {
            return resolve(document.querySelector(selector))
        }

        const observer = new MutationObserver(() => {
            if (document.querySelector(selector)) {
                observer.disconnect()
                return resolve(document.querySelector(selector))
            }
        })

        observer.observe(document.body, options)
    })
}
